'use client';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { IFilterObject } from '@/components/Product/Filters/types';
import FilterBox from '@/components/UI/Filter/FilterBox';
import {
  Text,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerProps,
  useDisclosure,
  Button,
  Flex,
} from '@chakra-ui/react';
import { FilterLineIcon } from '@/components/Icons';
import { filterGroups } from '@/components/Product/Filters/constants';
import FilterItemsGroup from '@/components/UI/Filter/FilterItemsGroup';
import ValueFilter from '@/components/Product/Filters/AllFilters/ValueFilter';
import { ANY_VALUE_RANGE } from '@/constants/product';
import { isEqual } from 'lodash';
import { IAppCategory } from '@/types/api/app/category';
import CategoriesFilter from '@/components/Product/Filters/AllFilters/CategoriesFilter';

interface MobileFiltersProps {
  valueRange?: number[];
  onValueRangeChange?: (range: number[]) => void;

  categories?: IAppCategory[];
  categoryOptions?: IAppCategory[];
  onCategoriesChange?: (categories: IAppCategory[]) => void;

  filters?: IFilterObject[];
  onFiltersChange?: (val: IFilterObject[]) => void;
}
const MobileFilters: FC<MobileFiltersProps> = ({
  valueRange = ANY_VALUE_RANGE,
  onValueRangeChange,
  categories = [],
  categoryOptions = [],
  onCategoriesChange,
  filters = [],
  onFiltersChange,
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const numOfApplyingFilters = useMemo(() => {
    if (isEqual(valueRange, ANY_VALUE_RANGE))
      return filters.length + categories.length;
    return filters.length + categories.length + 1;
  }, [filters, valueRange, categories]);

  return (
    <>
      <FiltersDrawer
        isOpen={isOpen}
        onClose={onClose}
        filters={filters}
        onFiltersChange={onFiltersChange}
        valueRange={valueRange}
        onValueRangeChange={onValueRangeChange}
        categories={categories}
        categoryOptions={categoryOptions}
        onCategoriesChange={onCategoriesChange}
      />
      <FilterBox
        isActive={!!numOfApplyingFilters}
        justifyContent="center"
        gap="0.25rem"
        fontSize="0.875rem"
        lineHeight="1.25rem"
        onClick={onOpen}
        hideClearIcon
        display={{ base: 'flex', lg: 'none' }}
      >
        <Text>Filter</Text>
        <FilterLineIcon size="1.25rem" />
        {!!numOfApplyingFilters && <Text>({numOfApplyingFilters})</Text>}
      </FilterBox>
    </>
  );
};

interface FiltersDrawerProps
  extends Omit<DrawerProps, 'children'>,
    MobileFiltersProps {}
const FiltersDrawer: FC<FiltersDrawerProps> = ({
  valueRange = ANY_VALUE_RANGE,
  onValueRangeChange,
  categories = [],
  categoryOptions = [],
  onCategoriesChange,
  filters = [],
  onFiltersChange,
  ...drawerProps
}) => {
  const { isOpen, onClose } = drawerProps;

  const [stagedFilters, setStagedFilters] = useState<IFilterObject[]>(filters);
  const [stagedCategories, setStagedCategories] =
    useState<IAppCategory[]>(categories);
  const [stagedValueRange, setStagedValueRange] =
    useState<number[]>(valueRange);

  const handleApply = useCallback(() => {
    onFiltersChange?.(stagedFilters);
    onValueRangeChange?.(stagedValueRange);
    onCategoriesChange?.(stagedCategories);
    onClose();
  }, [
    stagedFilters,
    stagedValueRange,
    stagedCategories,
    onFiltersChange,
    onClose,
    onValueRangeChange,
    onCategoriesChange,
  ]);

  useEffect(() => {
    setStagedFilters(filters);
    setStagedValueRange(valueRange);
    setStagedCategories(categories);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Drawer {...drawerProps}>
      <DrawerOverlay />
      <DrawerContent position="relative">
        <DrawerCloseButton />
        <DrawerHeader>
          <Text>Filter</Text>
        </DrawerHeader>

        <DrawerBody>
          <Flex flexDir="column" h="100%" p="1rem 0 2rem" gap="1rem">
            <ValueFilter
              range={stagedValueRange}
              onRangeChange={setStagedValueRange}
            />
            <FilterItemsGroup
              filters={stagedFilters}
              onFiltersChange={setStagedFilters}
              filterGroups={filterGroups}
            />
            {!!categoryOptions.length && (
              <CategoriesFilter
                categories={stagedCategories}
                categoryOptions={categoryOptions}
                onCategoriesChange={setStagedCategories}
              />
            )}
          </Flex>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="primaryMd" w="100%" onClick={handleApply}>
            Apply filters
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileFilters;
